import React, { useEffect, useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Minus, UtensilsCrossed, MessageSquare } from 'lucide-react';
import { useModifiers } from './ModifiersContext';
export type ItemDetailSheetProps = {
  open: boolean;
  item: {
    id: string;
    name: string;
    price: number;
    description?: string;
    image?: string;
    modifierIds?: string[];
  } | null;
  accent: string;
  onClose: () => void;
  onAdd: (item: {
    itemId: string;
    name: string;
    basePrice: number;
    modifiers: {
      id: string;
      name: string;
      price: number;
    }[];
    notes?: string;
  }, qty: number) => void;
};
function formatETB(n: number) {
  return `ETB ${Math.round(n).toLocaleString()}`;
}
export function ItemDetailSheet({
  open,
  item,
  accent,
  onClose,
  onAdd
}: ItemDetailSheetProps) {
  const {
    getModifier
  } = useModifiers();
  const [selectedModIds, setSelectedModIds] = useState<Set<string>>(new Set());
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState('');
  useEffect(() => {
    if (open) {
      setSelectedModIds(new Set());
      setQty(1);
      setNotes('');
    }
  }, [open, item]);
  if (!item) return null;
  const availableModifiers = (item.modifierIds || []).map(id => getModifier(id)).filter((m): m is NonNullable<typeof m> => m !== undefined && m.active);
  const toggleModifier = (modId: string) => {
    setSelectedModIds(prev => {
      const next = new Set(prev);
      if (next.has(modId)) {
        next.delete(modId);
      } else {
        next.add(modId);
      }
      return next;
    });
  };
  const selectedMods = availableModifiers.filter(m => selectedModIds.has(m.id));
  const unitPrice = item.price + selectedMods.reduce((sum, m) => sum + m.price, 0);
  const total = unitPrice * qty;
  const handleAdd = () => {
    onAdd({
      itemId: item.id,
      name: item.name,
      basePrice: item.price,
      modifiers: selectedMods.map(m => ({
        id: m.id,
        name: m.name,
        price: m.price
      })),
      notes: notes.trim() || undefined
    }, qty);
    onClose();
  };
  return <AnimatePresence data-id="element-2175">
      {open && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-40" data-id="element-2176" />
          <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        stiffness: 380,
        damping: 34
      }} className="fixed bottom-0 inset-x-0 z-50 bg-white rounded-t-3xl shadow-2xl max-w-md mx-auto flex flex-col max-h-[90vh]" role="dialog" aria-modal="true" data-id="element-2177">
            <div className="flex justify-center pt-3 pb-1 shrink-0" data-id="element-2178">
              <span className="w-10 h-1.5 rounded-full bg-gray-200" data-id="element-2179" />
            </div>

            <div className="flex items-start justify-between px-5 pt-2 pb-4 shrink-0" data-id="element-2180">
              <h2 className="text-lg font-extrabold text-gray-900 tracking-tight" data-id="element-2181">
                Customize
              </h2>
              <button onClick={onClose} aria-label="Close" className="p-1.5 -mr-1 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0" data-id="element-2182">
                <X size={20} data-id="element-2183" />
              </button>
            </div>

            <div className="overflow-y-auto px-5 pb-24" data-id="element-2184">
              <div className={`aspect-[4/3] bg-${accent}-50 rounded-2xl flex items-center justify-center text-${accent}-200 overflow-hidden mb-5`} data-id="element-2185">
                {item.image ? <img src={item.image} alt={item.name} loading="lazy" className="w-full h-full object-cover" data-id="element-2186" /> : <UtensilsCrossed size={40} data-id="element-2187" />}
              </div>

              <h3 className="text-xl font-bold text-gray-900 leading-snug mb-2" data-id="element-2188">
                {item.name}
              </h3>
              {item.description && <p className="text-sm text-gray-500 mb-4" data-id="element-2189">{item.description}</p>}
              <div className="font-bold text-gray-900 tabular-nums mb-6" data-id="element-2190">
                {formatETB(item.price)}
              </div>

              {availableModifiers.length > 0 && <div className="mb-6" data-id="element-2191">
                  <h4 className="font-bold text-gray-900 mb-3" data-id="element-2192">Add-ons</h4>
                  <div className="space-y-2" data-id="element-2193">
                    {availableModifiers.map(mod => {
                const isSelected = selectedModIds.has(mod.id);
                return <label key={mod.id} className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-colors ${isSelected ? `border-${accent}-500 bg-${accent}-50` : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-2194">
                          <div className="flex items-center gap-3" data-id="element-2195">
                            <input type="checkbox" checked={isSelected} onChange={() => toggleModifier(mod.id)} className={`w-5 h-5 rounded border-gray-300 text-${accent}-500 focus:ring-${accent}-500`} data-id="element-2196" />
                            <span className="text-sm font-medium text-gray-900" data-id="element-2197">
                              {mod.name}
                            </span>
                          </div>
                          <span className="text-sm text-gray-500 tabular-nums" data-id="element-2198">
                            {mod.price > 0 ? `+ ${formatETB(mod.price)}` : 'Free'}
                          </span>
                        </label>;
              })}
                  </div>
                </div>}

              <div className="mb-2" data-id="element-2199">
                <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2" data-id="element-2200">
                  <MessageSquare size={16} className="text-gray-400" data-id="element-2201" />
                  Special instructions
                  <span className="text-xs font-normal text-gray-400" data-id="element-2202">
                    · optional
                  </span>
                </h4>
                <textarea value={notes} onChange={e => setNotes(e.target.value.slice(0, 200))} rows={3} placeholder="e.g. no onions, extra crispy, allergy info…" className={`w-full px-3.5 py-3 rounded-xl bg-white border border-gray-200 text-sm resize-none focus:outline-none focus:border-${accent}-400 focus:ring-2 focus:ring-${accent}-100 transition placeholder:text-gray-400`} data-id="element-2203" />
                <div className="text-right text-[11px] text-gray-400 mt-1 tabular-nums" data-id="element-2204">
                  {notes.length}/200
                </div>
              </div>
            </div>

            <div className="absolute bottom-0 inset-x-0 bg-white border-t border-gray-100 p-4 flex items-center gap-4" data-id="element-2205">
              <div className="flex items-center gap-3 rounded-full bg-gray-100 p-1 shrink-0" data-id="element-2206">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center text-gray-600 hover:text-gray-900 transition-colors" data-id="element-2207">
                  <Minus size={18} data-id="element-2208" />
                </button>
                <span className="text-base font-bold tabular-nums w-4 text-center" data-id="element-2209">
                  {qty}
                </span>
                <button onClick={() => setQty(qty + 1)} className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center text-gray-600 hover:text-gray-900 transition-colors" data-id="element-2210">
                  <Plus size={18} data-id="element-2211" />
                </button>
              </div>

              <button onClick={handleAdd} className={`flex-1 py-3.5 rounded-full text-sm font-bold text-white transition-colors bg-${accent}-500 hover:bg-${accent}-600 flex items-center justify-center gap-2`} data-id="element-2212">
                Add to cart · {formatETB(total)}
              </button>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}